import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Music, Disc3, ExternalLink, X, Clock, User, ChevronRight } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const SpotifyIcon = () => (
  <svg className="w-4 h-4 text-[#1DB954]" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
  </svg>
);

const SpotifyNowPlaying = () => {
  const { themeColor } = useTheme();
  const [nowPlaying, setNowPlaying] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [progress, setProgress] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [recentTracks, setRecentTracks] = useState([]);
  const [topArtists, setTopArtists] = useState([]);

  useEffect(() => {
    const fetchNowPlaying = async () => {
      try {
        const response = await fetch('/api/spotify/now-playing', { cache: 'no-store' });
        if (response.ok) {
          const data = await response.json();
          setNowPlaying(data);
          setIsConnected(!data.needsAuth);
          if (data.progress && data.duration) {
            setProgress((data.progress / data.duration) * 100);
          }
        }
      } catch (_) { /* */ }
      finally { setIsLoading(false); }
    };
    fetchNowPlaying();
    const interval = setInterval(fetchNowPlaying, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchExpandedData = async () => {
    const [recentRes, artistsRes] = await Promise.all([
      fetch('/api/spotify/recently-played', { cache: 'no-store' }).catch(() => null),
      fetch('/api/spotify/top-artists', { cache: 'no-store' }).catch(() => null),
    ]);
    if (recentRes?.ok) { const d = await recentRes.json(); setRecentTracks(d.tracks || []); }
    if (artistsRes?.ok) { const d = await artistsRes.json(); setTopArtists(d.artists || []); }
  };

  const handleToggle = () => {
    if (!expanded) fetchExpandedData();
    setExpanded(!expanded);
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diffMin = Math.floor((now - d) / 60000);
    if (diffMin < 1) return 'Az önce';
    if (diffMin < 60) return `${diffMin} dk önce`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr} sa önce`;
    return `${Math.floor(diffHr / 24)} gün önce`;
  };

  if (isLoading) {
    return (
      <div className="w-full max-w-sm p-4 rounded-3xl glass animate-pulse">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-xl bg-zinc-800" />
          <div className="flex-1"><div className="h-4 bg-zinc-800 rounded w-3/4 mb-2" /><div className="h-3 bg-zinc-800 rounded w-1/2" /></div>
        </div>
      </div>
    );
  }

  if (!isConnected || nowPlaying?.needsAuth) {
    return (
      <div className="w-full max-w-sm p-4 rounded-3xl glass">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-[#1DB954]/10 flex items-center justify-center"><SpotifyIcon /></div>
          <div><p className="text-white font-medium text-sm">Spotify</p><p className="text-xs text-zinc-500">Bağlantı bekleniyor...</p></div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-sm">
      {/* Now Playing Card - Clickable */}
      <motion.div
        onClick={handleToggle}
        className="p-4 rounded-3xl glass glass-hover cursor-pointer gradient-border"
        whileTap={{ scale: 0.98 }}
        data-testid="spotify-now-playing"
      >
        <div className="flex items-center gap-4">
          <div className="relative flex-shrink-0">
            {nowPlaying?.albumImageUrl ? (
              <>
                <motion.img
                  src={nowPlaying.albumImageUrl}
                  alt={nowPlaying.album}
                  className="w-16 h-16 rounded-xl object-cover"
                  animate={nowPlaying.isPlaying ? { rotate: 360 } : {}}
                  transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                />
                {nowPlaying.isPlaying && (
                  <motion.div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-[#1DB954] flex items-center justify-center" animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 1, repeat: Infinity }}>
                    <Disc3 size={14} className="text-black" />
                  </motion.div>
                )}
              </>
            ) : (
              <div className="w-16 h-16 rounded-xl flex items-center justify-center" style={{ backgroundColor: `rgba(${themeColor.rgb}, 0.1)` }}>
                <Music size={24} style={{ color: themeColor.primary }} />
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <SpotifyIcon />
              <span className="text-xs text-zinc-500">{nowPlaying?.isPlaying ? 'Şu an dinliyor' : 'Son dinlenen'}</span>
            </div>
            {nowPlaying?.title ? (
              <>
                <p className="text-white font-medium truncate text-sm">{nowPlaying.title}</p>
                <p className="text-zinc-400 text-xs truncate">{nowPlaying.artist}</p>
              </>
            ) : (
              <p className="text-zinc-400 text-sm">Şu an müzik çalmıyor</p>
            )}
            {nowPlaying?.isPlaying && nowPlaying?.duration > 0 && (
              <div className="mt-2 h-1 bg-zinc-800 rounded-full overflow-hidden">
                <motion.div className="h-full bg-[#1DB954]" initial={{ width: 0 }} animate={{ width: `${progress}%` }} transition={{ duration: 0.5 }} />
              </div>
            )}
          </div>

          {nowPlaying?.isPlaying && (
            <div className="flex items-end gap-0.5 h-4 flex-shrink-0">
              {[1, 2, 3].map((bar) => (
                <motion.div key={bar} className="w-1 bg-[#1DB954] rounded-full" animate={{ height: ['4px', '16px', '8px', '12px', '4px'] }} transition={{ duration: 1, repeat: Infinity, delay: bar * 0.1 }} />
              ))}
            </div>
          )}

          <ChevronRight size={16} className={`text-zinc-500 transition-transform flex-shrink-0 ${expanded ? 'rotate-90' : ''}`} />
        </div>
      </motion.div>

      {/* Expanded Panel */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            className="mt-3 p-5 rounded-3xl glass-strong gradient-border overflow-hidden"
            initial={{ opacity: 0, height: 0, marginTop: 0 }}
            animate={{ opacity: 1, height: 'auto', marginTop: 12 }}
            exit={{ opacity: 0, height: 0, marginTop: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Recently Played */}
            <div className="mb-5">
              <div className="flex items-center gap-2 mb-3">
                <Clock size={14} style={{ color: themeColor.primary }} />
                <span className="text-sm font-medium text-white">Son Dinlenenler</span>
              </div>
              {recentTracks.length > 0 ? (
                <div className="space-y-2">
                  {recentTracks.map((track, i) => (
                    <a key={i} href={track.songUrl} target="_blank" rel="noreferrer" className="flex items-center gap-3 p-2 rounded-xl hover:bg-white/5 transition-all group">
                      {track.albumImage ? (
                        <img src={track.albumImage} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center flex-shrink-0"><Music size={16} className="text-zinc-600" /></div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm truncate group-hover:text-[#1DB954] transition-colors">{track.title}</p>
                        <p className="text-zinc-500 text-xs truncate">{track.artist}</p>
                      </div>
                      <span className="text-xs text-zinc-600 flex-shrink-0">{formatTime(track.playedAt)}</span>
                    </a>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-zinc-600 text-center py-3">Henüz veri yok</p>
              )}
            </div>

            {/* Top Artists */}
            {topArtists.length > 0 && (
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-3">
                  <User size={14} style={{ color: themeColor.primary }} />
                  <span className="text-sm font-medium text-white">Favori Sanatçılar</span>
                </div>
                <div className="flex gap-3 overflow-x-auto pb-1">
                  {topArtists.map((artist, i) => (
                    <a key={i} href={artist.url} target="_blank" rel="noreferrer" className="flex flex-col items-center gap-1.5 flex-shrink-0 group">
                      {artist.image ? (
                        <img src={artist.image} alt={artist.name} className="w-14 h-14 rounded-full object-cover ring-2 ring-transparent group-hover:ring-[#1DB954] transition-all" />
                      ) : (
                        <div className="w-14 h-14 rounded-full bg-zinc-800 flex items-center justify-center"><User size={20} className="text-zinc-600" /></div>
                      )}
                      <span className="text-xs text-zinc-400 text-center max-w-[60px] truncate group-hover:text-white transition-colors">{artist.name}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Spotify Profile Link */}
            <a
              href="https://open.spotify.com/user/canervac"
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-2 w-full py-2.5 rounded-2xl bg-[#1DB954]/10 text-[#1DB954] text-sm font-medium hover:bg-[#1DB954]/20 transition-all"
              data-testid="spotify-profile-link"
            >
              <SpotifyIcon /> Spotify Profilime Git <ExternalLink size={14} />
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SpotifyNowPlaying;
